import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import colors, { fonts, shadows } from '../theme';

export default function PricingPage() {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const plans = [
    {
      name: 'Free',
      price: { monthly: 0, annual: 0 },
      description: 'Perfect for small teams getting started',
      features: [
        'Up to 5 users',
        'Unlimited knowledge entries',
        'AI-generated quizzes',
        'Basic integrations',
        'Community support',
      ],
      cta: 'Get Started Free',
      highlighted: false,
    },
    {
      name: 'Pro',
      price: { monthly: 8, annual: 80 },
      priceUnit: 'per user',
      description: 'Scale with your team',
      features: [
        '6-50 users',
        'Everything in Free',
        'Priority support',
        'Advanced integrations',
        'Usage analytics',
        'Cancel anytime',
      ],
      cta: 'Start Growing',
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: { monthly: 399, annual: 4788 },
      description: 'For larger organizations',
      features: [
        'Unlimited users',
        'Everything in Per-User',
        'Dedicated support',
        'Custom integrations',
        'SLA guarantee',
        'Advanced security',
      ],
      cta: 'Contact Sales',
      highlighted: false,
    },
  ];

  const calculateSavings = (annual, monthly) => {
    const monthlyCost = monthly * 12;
    const savings = monthlyCost - annual;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return { savings, percentage };
  };

  return (
    <div style={{
      fontFamily: fonts.landing,
      background: colors.landing.bg,
      minHeight: '100vh',
      color: colors.landing.text,
    }}>
      <style>{`
        .gradient-text {
          background: ${colors.gradients.coral};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .plan-card {
          transition: all 0.3s ease;
        }
        .plan-card:hover {
          transform: translateY(-8px);
        }
      `}</style>

      {/* Header/Nav */}
      <nav style={{
        padding: '24px 48px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${colors.overlay.coralBorder}`,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: 'pointer',
        }}
        onClick={() => navigate('/')}>
          <img
            src="/logo192.png"
            alt="Miblio"
            style={{
              width: '40px',
              height: '40px',
            }}
          />
          <span style={{
            fontSize: '24px',
            fontWeight: 800,
          }} className="gradient-text">
            miblio
          </span>
        </div>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <button onClick={() => navigate('/login')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.text,
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 600,
          }}>
            Sign In
          </button>
          <button onClick={() => navigate('/register')} style={{
            background: colors.gradients.coral,
            border: 'none',
            color: colors.white,
            padding: '12px 32px',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 700,
            boxShadow: shadows.coralMd,
          }}>
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        padding: '80px 48px 60px',
        textAlign: 'center',
        maxWidth: '900px',
        margin: '0 auto',
      }}>
        <h1 style={{
          fontSize: '64px',
          fontWeight: 800,
          lineHeight: 1.1,
          marginBottom: '24px',
          background: colors.gradients.coral,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          Simple, Transparent Pricing
        </h1>

        <p style={{
          fontSize: '22px',
          lineHeight: 1.6,
          opacity: 0.9,
          marginBottom: '48px',
        }}>
          Choose the plan that's right for your team. Start free, scale as you grow.
        </p>

        {/* Billing Toggle */}
        <div style={{
          display: 'inline-flex',
          background: colors.white,
          borderRadius: '12px',
          padding: '6px',
          boxShadow: shadows.md,
          marginBottom: '48px',
        }}>
          <button
            onClick={() => setBillingPeriod('monthly')}
            style={{
              padding: '12px 32px',
              border: 'none',
              borderRadius: '8px',
              background: billingPeriod === 'monthly' ? colors.gradients.coral : 'transparent',
              color: billingPeriod === 'monthly' ? colors.white : colors.gray[600],
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('annual')}
            style={{
              padding: '12px 32px',
              border: 'none',
              borderRadius: '8px',
              background: billingPeriod === 'annual' ? colors.gradients.coral : 'transparent',
              color: billingPeriod === 'annual' ? colors.white : colors.gray[600],
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              position: 'relative',
            }}
          >
            Annual
            {billingPeriod === 'annual' && (
              <span style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                background: colors.landing.coral,
                color: colors.white,
                fontSize: '10px',
                fontWeight: 800,
                padding: '4px 8px',
                borderRadius: '6px',
                textTransform: 'uppercase',
              }}>
                Save 17%
              </span>
            )}
          </button>
        </div>
      </section>

      {/* Pricing Cards */}
      <section style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 48px 80px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '32px',
      }}>
        {plans.map((plan, index) => {
          const price = plan.price[billingPeriod];
          const savings = plan.priceUnit && billingPeriod === 'annual'
            ? calculateSavings(plan.price.annual, plan.price.monthly)
            : null;

          return (
            <div
              key={index}
              className="plan-card"
              style={{
                background: colors.white,
                borderRadius: '24px',
                padding: '48px 32px',
                boxShadow: plan.highlighted ? shadows.coralLg : shadows.md,
                border: plan.highlighted ? `3px solid ${colors.landing.coral}` : '1px solid transparent',
                position: 'relative',
              }}
            >
              {plan.highlighted && (
                <div style={{
                  position: 'absolute',
                  top: '-16px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: colors.gradients.coral,
                  color: colors.white,
                  padding: '8px 24px',
                  borderRadius: '24px',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}>
                  Most Popular
                </div>
              )}

              <h3 style={{
                fontSize: '28px',
                fontWeight: 800,
                marginBottom: '12px',
                color: colors.gray[900],
              }}>
                {plan.name}
              </h3>

              <p style={{
                fontSize: '16px',
                color: colors.gray[600],
                marginBottom: '32px',
                minHeight: '48px',
              }}>
                {plan.description}
              </p>

              <div style={{ marginBottom: '32px' }}>
                <div style={{
                  fontSize: '56px',
                  fontWeight: 800,
                  color: colors.gray[900],
                  lineHeight: 1,
                }}>
                  ${price}
                  {plan.priceUnit && (
                    <span style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: colors.gray[600],
                    }}>
                      /{plan.priceUnit}
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: '16px',
                  color: colors.gray[600],
                  marginTop: '8px',
                }}>
                  {billingPeriod === 'monthly' ? 'per month' : 'per year'}
                </div>
                {savings && (
                  <div style={{
                    fontSize: '14px',
                    color: colors.landing.coral,
                    fontWeight: 600,
                    marginTop: '8px',
                  }}>
                    Save ${savings.savings}/year ({savings.percentage}% off)
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  if (plan.name === 'Free') {
                    navigate('/register?plan=free');
                  } else if (plan.name === 'Enterprise') {
                    navigate(`/register?plan=enterprise&billing=${billingPeriod}`);
                  } else {
                    // Per-User plan
                    navigate(`/register?plan=per_user&billing=${billingPeriod}`);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '16px',
                  border: plan.highlighted ? 'none' : `2px solid ${colors.gray[300]}`,
                  borderRadius: '12px',
                  background: plan.highlighted ? colors.gradients.coral : 'transparent',
                  color: plan.highlighted ? colors.white : colors.gray[700],
                  fontSize: '18px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginBottom: '32px',
                  boxShadow: plan.highlighted ? shadows.coralMd : 'none',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) => {
                  if (plan.highlighted) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = shadows.coralLg;
                  } else {
                    e.currentTarget.style.borderColor = colors.landing.coral;
                    e.currentTarget.style.color = colors.landing.coral;
                  }
                }}
                onMouseOut={(e) => {
                  if (plan.highlighted) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = shadows.coralMd;
                  } else {
                    e.currentTarget.style.borderColor = colors.gray[300];
                    e.currentTarget.style.color = colors.gray[700];
                  }
                }}
              >
                {plan.cta}
              </button>

              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
              }}>
                {plan.features.map((feature, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: '16px',
                    fontSize: '15px',
                    color: colors.gray[700],
                    lineHeight: 1.6,
                  }}>
                    <span style={{
                      fontSize: '18px',
                      color: colors.landing.coral,
                      flexShrink: 0,
                    }}>
                      ✓
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>

      {/* FAQ Section */}
      <section style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '80px 48px',
      }}>
        <h2 style={{
          fontSize: '48px',
          fontWeight: 800,
          textAlign: 'center',
          marginBottom: '48px',
          background: colors.gradients.coral,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          Frequently Asked Questions
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {[
            {
              q: 'Can I change plans at any time?',
              a: 'Yes! You can upgrade instantly with prorated billing. Downgrades take effect at the end of your billing period.',
            },
            {
              q: 'What happens if I exceed my user limit?',
              a: "You'll be prompted to upgrade to the next tier. New users won't be able to join until you upgrade or remove existing users.",
            },
            {
              q: 'Is there a free trial for paid plans?',
              a: 'No need for a trial! Start with our generous Free plan (up to 5 users) and upgrade when you need more seats.',
            },
            {
              q: 'How does annual billing work?',
              a: "You pay upfront for the full year and save 17% compared to monthly. It's billed as one payment per year.",
            },
            {
              q: 'What payment methods do you accept?',
              a: 'We accept all major credit cards through Stripe. Enterprise customers can request invoice billing.',
            },
          ].map((faq, index) => (
            <div key={index} style={{
              background: colors.white,
              padding: '32px',
              borderRadius: '16px',
              boxShadow: shadows.md,
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 700,
                marginBottom: '12px',
                color: colors.gray[900],
              }}>
                {faq.q}
              </h3>
              <p style={{
                fontSize: '16px',
                lineHeight: 1.6,
                color: colors.gray[700],
                margin: 0,
              }}>
                {faq.a}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '48px',
        textAlign: 'center',
        background: colors.landing.bg,
        borderTop: `1px solid ${colors.overlay.coralBorder}`,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '16px',
        }}>
          <img
            src="/logo192.png"
            alt="Miblio"
            style={{
              width: '32px',
              height: '32px',
            }}
          />
          <span style={{
            fontSize: '24px',
            fontWeight: 800,
          }} className="gradient-text">
            miblio
          </span>
        </div>
        <div style={{
          display: 'flex',
          gap: '24px',
          justifyContent: 'center',
          marginBottom: '16px',
        }}>
          <button onClick={() => navigate('/')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Home
          </button>
          <button onClick={() => navigate('/pricing')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Pricing
          </button>
          <button onClick={() => navigate('/security')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Security
          </button>
          <button onClick={() => navigate('/privacy')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Privacy
          </button>
          <button onClick={() => navigate('/terms')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Terms
          </button>
        </div>
        <p style={{
          fontSize: '14px',
          color: colors.landing.textLight,
        }}>
          © 2024 Miblio. Built for teams who learn together.
        </p>
      </footer>
    </div>
  );
}
